import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, FileText, BarChart3, ListChecks, LayoutGrid } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

interface CreatePageModalProps {
  open: boolean;
  onClose: () => void;
  onGenerateWithAI: (prompt: string) => Promise<void>;
}

export default function CreatePageModal({
  open,
  onClose,
  onGenerateWithAI,
}: CreatePageModalProps) {
  const navigate = useNavigate();
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("ai");

  const examplePrompts = [
    {
      title: "Sistema de Membros",
      prompt: "Crie um sistema de controle de membros com dashboard principal mostrando total de membros, membros ativos e novos este mês, uma página de lista completa com filtros por status e botão de cadastro, e página de estatísticas com gráficos de distribuição",
    },
    {
      title: "Dashboard Financeiro",
      prompt: "Dashboard financeiro com cards de receita, despesa e lucro do mês, gráfico de linha mostrando evolução mensal dos últimos 6 meses, e tabela com últimas transações incluindo filtro por período",
    },
    {
      title: "Gestão de Projetos",
      prompt: "Sistema de gestão de projetos com dashboard de métricas (projetos ativos, concluídos e atrasados), quadro kanban para tarefas com colunas A Fazer, Em Progresso e Concluído, e página de relatórios com gráficos de progresso",
    },
    {
      title: "Dashboard de Vendas",
      prompt: "Dashboard de vendas com métricas principais (vendas do mês, ticket médio, taxa de conversão), gráfico de vendas mensais dos últimos 12 meses, e tabela de produtos mais vendidos com filtros",
    },
  ];

  const pageTemplates = [
    {
      id: 'empty',
      name: 'Página Vazia',
      description: 'Comece do zero com uma página em branco',
      icon: <FileText className="w-8 h-8" />,
    },
    {
      id: 'analytics',
      name: 'Dashboard Analytics',
      description: 'Métricas, gráficos e KPIs para análise de dados',
      icon: <BarChart3 className="w-8 h-8" />,
    },
    {
      id: 'crud',
      name: 'Gerenciamento de Dados',
      description: 'Tabela com dados e botão para adicionar registros',
      icon: <ListChecks className="w-8 h-8" />,
    },
    {
      id: 'kanban',
      name: 'Kanban Board',
      description: 'Quadro com colunas e cards arrastáveis',
      icon: <LayoutGrid className="w-8 h-8" />,
    },
  ];

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) return;

    setIsGenerating(true);
    try {
      await onGenerateWithAI(aiPrompt);
      setAiPrompt("");
      onClose();
    } catch (error) {
      console.error("Erro ao gerar páginas:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    onClose();
    // Navegar para o page builder com o template selecionado via state
    navigate("/pages/new", { state: { templateId } });
  };

  const handleClose = () => {
    setAiPrompt("");
    setActiveTab("ai");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[750px] p-0 gap-0 overflow-hidden max-h-[85vh]">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b">
            <h2 className="text-2xl font-semibold tracking-tight mb-4">
              Criar Nova Página
            </h2>
            <TabsList className="w-full grid grid-cols-2 h-11 p-1 bg-muted">
              <TabsTrigger 
                value="ai" 
                className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Sparkles className="h-4 w-4" />
                Gerar com IA
              </TabsTrigger>
              <TabsTrigger 
                value="manual" 
                className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <FileText className="h-4 w-4" />
                Templates
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Aba: Criar com IA */}
          <TabsContent value="ai" className="m-0 flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Textarea */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Descreva o que você precisa
                </label>
                <Textarea
                  placeholder="Ex: Dashboard financeiro com cards de métricas, gráfico de evolução mensal e tabela de transações..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={4}
                  disabled={isGenerating}
                  className="resize-none bg-muted/50 focus-visible:ring-1 text-[15px] leading-relaxed"
                />
              </div>

              {/* Templates */}
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Exemplos de Prompts
                </p>
                <div className="grid gap-2">
                  {examplePrompts.map((example, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => !isGenerating && setAiPrompt(example.prompt)}
                      disabled={isGenerating}
                      className="text-left px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                      <p className="text-sm font-medium group-hover:text-primary transition-colors">
                        {example.title}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t bg-muted/30 flex items-center justify-between sticky bottom-0">
              <p className="text-xs text-muted-foreground">
                A IA criará páginas completas com componentes
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isGenerating}
                  size="sm"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleGenerateAI}
                  disabled={!aiPrompt.trim() || isGenerating}
                  size="sm"
                  className="gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Gerar Páginas
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Aba: Criar Manualmente */}
          <TabsContent value="manual" className="m-0 flex-1 overflow-y-auto">
            <div className="px-8 py-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Escolha um template para começar ou crie uma página vazia
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pageTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleSelectTemplate(template.id)}
                      className="flex flex-col items-start p-5 border border-border rounded-lg hover:border-primary hover:bg-accent transition-all text-left group h-full"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-primary group-hover:scale-110 transition-transform">
                          {template.icon}
                        </div>
                        <h3 className="text-base font-semibold group-hover:text-primary transition-colors">
                          {template.name}
                        </h3>
                      </div>
                      <p className="text-sm text-muted-foreground flex-1">
                        {template.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t bg-muted/30 flex justify-end gap-2 sticky bottom-0">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                size="sm"
              >
                Cancelar
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
